import { useInfiniteQuery } from '@tanstack/react-query';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { isEmpty, startCase, uniqBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useGrowthMarginBridgesContext } from '../GrowthMarginBridgesContext';
import { formatRawNumberForExport, getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';

const useDecompositionChartData = (selectedDecompositionLevel: any) => {
    const [decompositionChartData, setDecompositionChartData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    const { selectedBridgesKPI, isPercentageUnitSelected, previousYearGMBData } = useGrowthMarginBridgesContext();
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { growthBridgesKpiLabelMapping } = profitPoolConfig;
    const exportDecimals = getConfig('commonConfig', appConfig)?.exportDecimalPlaces ?? 7;

    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);
    const {
        data,
        isPending: isDecompositionChartPending,
        isError: isDecompositionChartError,
        isSuccess: isDecompositionChartSuccess,
        fetchNextPage: fetchDecompositionChartNextPage,
        hasNextPage: hasNextDecompositionChartPage,
        isFetchingNextPage: isFetchingDecompositionChartNextPage,
    } = useInfiniteQuery({
        queryKey: [
            'decomposition-chart-infi',
            selectedDecompositionLevel?.dataKey,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter: selectedDecompositionLevel,
                offset: pageParam as number,
                kpi: 'growth_margin_bridges',
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                limit: PROFIT_POOL_API_LIMIT,
            });
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                selectedBridgesKPI &&
                selectedBridgesKPI !== 'mix_impact',
        ),
    });

    useEffect(() => {
        if (!isDecompositionChartPending && data?.pages) {
            const allData = data.pages.flatMap((page: any) => page?.data || []);

            if (!isEmpty(allData)) {
                let minProfit = Number.POSITIVE_INFINITY;
                let maxProfit = Number.NEGATIVE_INFINITY;

                const finalChartData = allData.map((item: any) => {
                    const currentProfit = isPercentageUnitSelected
                        ? (item.kpi_values?.[selectedBridgesKPI] / previousYearGMBData) * 100
                        : item.kpi_values?.[selectedBridgesKPI] || 0;
                    if (currentProfit < minProfit) minProfit = currentProfit;
                    if (currentProfit > maxProfit) maxProfit = currentProfit;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc ?? item.attribute_ex_id,
                        [selectedBridgesKPI]: currentProfit,
                        tier: item.tier,
                        colorKey: '#5F49BC',
                    };
                });

                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);

                setDecompositionChartData(uniqBy(finalChartData, 'attribute_ex_id'));
            } else {
                setDecompositionChartData([]);
                setMin(0);
                setMax(0);
            }
        }
    }, [isDecompositionChartPending, data, selectedBridgesKPI, isPercentageUnitSelected, previousYearGMBData]);

    const excelExportData = useMemo(() => {
        if (!decompositionChartData?.length) {
            return { headers: [], data: [] };
        }

        const headers = [
            'Attribute',
            isPercentageUnitSelected
                ? `${growthBridgesKpiLabelMapping[selectedBridgesKPI] || startCase(selectedBridgesKPI)} Growth`
                : growthBridgesKpiLabelMapping[selectedBridgesKPI] || startCase(selectedBridgesKPI),
        ];

        const data = decompositionChartData.map((item: any) => [
            item.attributeDesc || '',
            isPercentageUnitSelected
                ? `${item[selectedBridgesKPI]?.toFixed(2)}%`
                : formatRawNumberForExport(item[selectedBridgesKPI], exportDecimals),
        ]);

        return { headers, data };
    }, [decompositionChartData, selectedBridgesKPI, isPercentageUnitSelected, exportDecimals]);

    return {
        decompositionChartData,
        excelExportData,
        isPending: isDecompositionChartPending,
        isError: isDecompositionChartError,
        isSuccess: isDecompositionChartSuccess,
        fetchNextPage: fetchDecompositionChartNextPage,
        hasNextPage: hasNextDecompositionChartPage,
        isFetchingNextPage: isFetchingDecompositionChartNextPage,
        min,
        max,
    };
};

export default useDecompositionChartData;
